import React, { PropsWithChildren, useRef, useState, useEffect } from 'react';
import Card from 'lib/Card';
import { CardTheme } from 'lib/Card/Card.types';

interface Props {
  header: string;
  extraHeaderContent?: React.ReactElement | null;
  isVisible?: boolean;
  className?: string;
  theme?: CardTheme;
  customCardHeight?: number;
}

export const DealSummaryCard: React.FC<PropsWithChildren<Props>> = ({
  header,
  extraHeaderContent,
  isVisible = true,
  className,
  theme = CardTheme.DEFAULT,
  customCardHeight,
  children,
}) => {
  const cardRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const [measuredHeight, setMeasuredHeight] = useState(0);

  useEffect(() => {
    if (!cardRef.current) return undefined;

    const observer = new ResizeObserver(([entry]: ResizeObserverEntry[]) => {
      setMeasuredHeight(entry.contentRect.height);
    });

    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  const cardHeight = customCardHeight || measuredHeight;
  const cardAnimation = {
    transition: `transition-[height,margin,opacity] ease-in-out duration-150 ${
      isVisible ? `opacity-100 ${className}` : 'opacity-0 invisible -mt-3'
    }`,
    height: `${isVisible ? cardHeight : 0}px`,
  };

  return (
    <div
      data-test-id={`${header.replace(/\s/g, '-').toLowerCase()}-card-container`}
      className={cardAnimation.transition}
      style={{ height: cardAnimation.height }}
    >
      <Card cardRef={cardRef} header={header} extraHeaderContent={extraHeaderContent} theme={theme}>
        {children}
      </Card>
    </div>
  );
};
